import { CheckOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import { Button, Empty } from 'antd';
import ActionSheet, { SheetAction } from 'components/UI/Responsive/ActionSheet';
import * as CardList from 'components/UI/Responsive/CardList';
import SelectionBar, {
  SelectionBarSpace,
} from 'components/UI/Responsive/SelectionBar';
import { useEndOfList } from 'hooks/layout/useEndOfList';
import { OrderIntention } from 'model/OrderIntention';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  buyIntenteionSelectors,
  sessionSelectors,
} from 'store/state/selectors';
import {
  DetailAction,
  OrderIntentionDetailSheet,
} from '../Detail/OrderIntentionDetail';
import {
  canOpenJustification,
  isSelectionBlocked,
} from '../List/orderIntentionRules';
import {
  OrderIntentionAction,
  useOrderIntentionBulkActions,
  useOrderIntentionRowActions,
} from '../List/useOrderIntentionActions';
import { OrderIntentionSelection } from '../List/useOrderIntentionSelection';
import OrderIntentionCard, {
  OrderIntentionCardSkeleton,
} from './OrderIntentionCard';

import * as S from './styles';

const BLOCK_SIZE = 20;

interface Props {
  orders: OrderIntention[];
  isLoading: boolean;
  hasSearched: boolean;
  selection: OrderIntentionSelection;
  onOpenJustification: (order: OrderIntention) => void;
}

const BULK_ACTION_DECORATION: Record<
  string,
  { icon: ReactNode; hint: string; tone?: SheetAction['tone'] }
> = {
  approve: {
    icon: <CheckOutlined />,
    hint: 'Aprovar as vendas diretas selecionadas',
    tone: 'success',
  },
  republish: {
    icon: <ReloadOutlined />,
    hint: 'Publicar as intenções de compra de novo',
  },
  cancel: {
    icon: <StopOutlined />,
    hint: 'Cancelar as intenções de compra selecionadas',
    tone: 'danger',
  },
};

const DETAIL_ACTION_TONES: Record<string, DetailAction['tone']> = {
  approve: 'primary',
  republish: 'primary',
  refuse: 'danger',
  cancel: 'danger',
};

const toSheetAction = (action: OrderIntentionAction): SheetAction => ({
  ...action,
  ...BULK_ACTION_DECORATION[action.key],
});

const renderPlaceholders = (count: number, label: string) => (
  <CardList.Cards role="status" aria-label={label}>
    {Array.from({ length: count }, (_, index) => (
      <OrderIntentionCardSkeleton key={index} />
    ))}
  </CardList.Cards>
);

export default function OrderIntentionsCardList({
  orders,
  isLoading,
  hasSearched,
  selection,
  onOpenJustification,
}: Props) {
  const [visibleCount, setVisibleCount] = useState(BLOCK_SIZE);
  const [detailedOrder, setDetailedOrder] = useState<OrderIntention | null>(
    null,
  );
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const getRowActions = useOrderIntentionRowActions();
  const bulkActions = useOrderIntentionBulkActions();
  const customer = useSelector(buyIntenteionSelectors.getCustomer);
  const selectedCustomerIdFilter = useSelector(
    sessionSelectors.getSelectedCustomerIdFilter,
  );
  const [firstOrder] = orders;

  useEffect(() => {
    setVisibleCount(BLOCK_SIZE);
  }, [firstOrder]);

  const hasMore = visibleCount < orders.length;
  const showMore = useCallback(() => {
    if (hasMore) setVisibleCount((count) => count + BLOCK_SIZE);
  }, [hasMore]);
  const endOfListRef = useEndOfList(showMore, visibleCount);

  const describeDetailActions = (order: OrderIntention): DetailAction[] => {
    const actions: DetailAction[] = getRowActions(order)
      .filter((action) => action.key !== 'details')
      .map((action) => ({ ...action, tone: DETAIL_ACTION_TONES[action.key] }));
    if (canOpenJustification(order.origin, order.status, order.justification)) {
      actions.push({
        key: 'justification',
        label: 'Justificativa',
        onSelect: () => onOpenJustification(order),
      });
    }
    return actions;
  };

  if (isLoading && orders.length === 0) {
    return renderPlaceholders(4, 'Carregando pedidos');
  }

  if (orders.length === 0) {
    return (
      <Empty
        description={
          hasSearched
            ? 'Nenhum pedido encontrado'
            : 'Use os filtros para buscar seus pedidos'
        }
      />
    );
  }

  return (
    <>
      <CardList.List>
        <CardList.Toolbar>
          <CardList.Count>
            {orders.length.toLocaleString('pt-BR')} pedidos
          </CardList.Count>
        </CardList.Toolbar>
        {selection.selectableCount > 0 && (
          <SelectionBar
            label="Seleção de pedidos"
            selectedCount={selection.selectedOrderIntentions.length}
            totalCount={selection.selectableCount}
            isToggleAllDisabled={selection.hideSelectAll}
            onToggleAll={selection.toggleAll}
            onClear={() => selection.onChangeSelecteds([], [])}
          >
            <Button
              type="primary"
              disabled={bulkActions.length === 0}
              onClick={() => setIsBulkOpen(true)}
            >
              Ações em lote
            </Button>
          </SelectionBar>
        )}
        <CardList.Cards aria-label="Pedidos">
          {orders.slice(0, visibleCount).map((order) => (
            <OrderIntentionCard
              key={order.id}
              order={order}
              branch={customer?.branch}
              showsCustomer={!selectedCustomerIdFilter}
              isSelectable={!isSelectionBlocked(order.status, order.origin)}
              isSelected={selection.isSelected(order)}
              isSelectionDisabled={
                !selection.isSelected(order) && selection.disableCheckbox(order)
              }
              onSelect={() => selection.toggleOrder(order)}
              onClick={() => setDetailedOrder(order)}
            />
          ))}
        </CardList.Cards>
        {!hasMore && <S.Hint>Todos os pedidos foram carregados</S.Hint>}
        <CardList.EndOfList ref={endOfListRef} />
        {selection.selectedOrderIntentions.length > 0 && <SelectionBarSpace />}
      </CardList.List>

      <ActionSheet
        isOpen={isBulkOpen}
        onClose={() => setIsBulkOpen(false)}
        title="Ações em lote"
        description={`${selection.selectedOrderIntentions.length.toLocaleString(
          'pt-BR',
        )} pedidos selecionados`}
        actions={bulkActions.map(toSheetAction)}
      />
      <OrderIntentionDetailSheet
        order={detailedOrder}
        actions={detailedOrder ? describeDetailActions(detailedOrder) : []}
        onClose={() => setDetailedOrder(null)}
      />
    </>
  );
}
